import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SEOHead } from '../../components/seo/SEOHead';
import Button from '../../components/base/Button';
import { supabase } from '../../lib/supabase';
import toast from '../../utils/toast';

interface LocationState {
  email?: string;
}

export default function EmailSentPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { email = '' } = (location.state as LocationState) || {};
  const [resending, setResending] = useState(false);

  const handleResendEmail = async () => {
    if (!email) {
      toast.error('Email não encontrado. Faça o cadastro novamente.');
      navigate('/signup');
      return;
    }

    setResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/signup/confirmed`,
        },
      });

      if (error) {
        throw error;
      }

      toast.success('📧 Enviamos o email novamente! Confira sua caixa de entrada.');
    } catch (err) {
      console.error('Erro ao reenviar email:', err);
      toast.error('Não foi possível reenviar o email. Tente novamente em instantes.');
    } finally {
      setResending(false);
    }
  };

  const handleEditEmail = () => {
    navigate('/signup');
  };

  if (!email) {
    navigate('/signup');
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-white p-6">
      <SEOHead
        title="Confirme seu email - Magistral"
        description="Enviamos um email para confirmar sua conta. Confira a caixa de entrada para continuar o cadastro."
        robots="noindex, nofollow"
      />

      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl overflow-hidden">
            <img
              src="/Imagens/logo magistral 2.0 verde.png"
              alt="Magistral"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-mail-line text-blue-600 text-4xl"></i>
          </div>

          <h1 className="text-2xl font-bold text-gray-800 text-center mb-3">
            Enviamos um email para você! 📧
          </h1>

          <p className="text-center text-gray-600 mb-6">
            Confira sua caixa de entrada em
          </p>
          <div className="bg-gray-100 rounded-xl p-3 mb-6 text-center">
            <p className="font-mono text-sm text-gray-800 break-all">{email}</p>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 mb-6">
            <p className="font-semibold text-gray-800 mb-3">Como continuar:</p>
            <ol className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  1
                </span>
                <span>
                  Abra seu <strong>e-mail</strong> (Gmail, Outlook, etc.)
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  2
                </span>
                <span>
                  Procure por um email de <strong>"Magistral Homeopatia"</strong>
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  3
                </span>
                <span>Clique no botão <strong>"Confirmar Email"</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  4
                </span>
                <span>Uma nova aba abrirá automaticamente no navegador</span>
              </li>
            </ol>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-2">
              <i className="ri-error-warning-line text-yellow-600 text-xl flex-shrink-0"></i>
              <div>
                <p className="font-semibold text-gray-800 text-sm mb-1">⚠️ Não recebeu o email?</p>
                <p className="text-xs text-gray-700">
                  Confira também a pasta <strong>SPAM</strong> ou <strong>Promoções</strong>
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Button onClick={handleResendEmail} disabled={resending} variant="secondary" className="w-full">
              {resending ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Reenviando...
                </>
              ) : (
                <>
                  <i className="ri-refresh-line mr-2"></i>
                  Reenviar Email
                </>
              )}
            </Button>

            <button
              onClick={handleEditEmail}
              className="w-full text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              Digitou o email errado? <span className="underline">Corrigir</span>
            </button>
          </div>
        </div>

        <div className="text-center text-sm text-gray-500">
          <p>💡 Dica: Não feche esta aba enquanto não confirmar o email</p>
        </div>
      </div>
    </div>
  );
}
